import React, { useState } from 'react';
import { Gift } from 'lucide-react';

/**
 * ProductImage — resilient image component for product cards.
 *
 * Many of our seeded products have stale Amazon CDN URLs that 404. This
 * component handles all the failure modes gracefully:
 *
 *   - No `src` at all → soft cream placeholder with a Gift icon
 *   - `src` set but image fails to load → same placeholder (onError)
 *   - Image succeeds → renders normally with a brief fade-in
 *
 * The placeholder is a pure-CSS visual (no network request), so it
 * survives offline / CDN-down scenarios too.
 */
const Placeholder = ({ label }) => (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-cream-100 text-clay-300"
    aria-hidden
  >
    <Gift className="h-8 w-8" strokeWidth={1.5} />
    {label && (
      <span className="text-[10.5px] uppercase tracking-[0.14em] text-ink-muted/80 px-4 text-center line-clamp-2">
        {label}
      </span>
    )}
  </div>
);

export const ProductImage = ({
  src,
  alt = '',
  fallbackLabel,
  className = 'h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]',
  loading = 'lazy',
  ...rest
}) => {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // No src or already-failed → show placeholder only
  if (!src || errored) {
    return <Placeholder label={fallbackLabel} />;
  }

  return (
    <>
      {/* Placeholder shows until the image has loaded */}
      {!loaded && <Placeholder label={fallbackLabel} />}
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        {...rest}
      />
    </>
  );
};

export default ProductImage;
